import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import SettingsBrightnessRoundedIcon from '@mui/icons-material/SettingsBrightnessRounded';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import { Suspense, useEffect, useRef, useState } from 'react';
import type { ComponentType } from 'react';
import { NavLink, Outlet, useLocation, useMatch, useNavigate } from 'react-router-dom';
import { nombreParaMostrar, type EstadoComercial, type Modulo, type Permiso } from '@exp/contracts';
import { Logo } from '../marca/Logo';
import { FirmaDePulso } from '../producto/MarcaDePulso';
import { ARMAZON_PULSO, FICHA_DE_PULSO } from '../producto/identidad';
import { useMarca } from '../marca/ProveedorDeMarca';
import { useApariencia } from './Apariencia';
import { useAtajo } from './atajos';
import { BuscadorDePaciente } from './BuscadorDePaciente';
import { Cargando, Tecla } from './comunes';
import { CintaDePaciente } from './CintaDePaciente';
import { iniciales } from './iniciales';
import { FronteraDeContenido } from './FronteraDeError';
import { CHASIS } from './tokens';
import { useSesion } from '../sesion/SesionProvider';
import type { PreferenciaDeTema } from '../marca/tema';

/** Opciones del menu de tema. Automatico sigue al sistema en vivo. */
const OPCIONES_TEMA: ReadonlyArray<{
  valor: PreferenciaDeTema;
  texto: string;
  Icono: ComponentType<{ fontSize?: 'small' }>;
}> = [
  { valor: 'claro', texto: 'Claro', Icono: LightModeRoundedIcon },
  { valor: 'oscuro', texto: 'Oscuro', Icono: DarkModeRoundedIcon },
  { valor: 'automatico', texto: 'Automático', Icono: SettingsBrightnessRoundedIcon },
];

const ROLES_LEGIBLES: Record<string, string> = {
  propietario: 'Propietario',
  medico: 'Médico',
  asistente: 'Asistente',
};

interface Destino {
  a: string;
  texto: string;
  Icono: ComponentType<{ fontSize?: 'small' | 'inherit' }>;
  /** `null`: cualquiera con sesion. */
  permiso: Permiso | null;
  /** Tecla suelta que lleva aqui. Se anuncia en el tooltip. */
  atajo?: string;
  /** Coincide tambien con las subrutas. */
  parcial?: boolean;
  /** Solo si el consultorio tiene contratado el modulo (F4.7). */
  modulo?: Modulo;
}

/**
 * Destinos, en dos grupos que dicen algo cierto: arriba donde se trabaja,
 * abajo como esta puesto el consultorio. La administracion no se mezcla con
 * la tarea: quien no administra no ve el segundo grupo mas que en lectura.
 */
const TRABAJO: readonly Destino[] = [
  { a: '/', texto: 'Hoy', Icono: HomeOutlinedIcon, permiso: null, atajo: 'h' },
  {
    a: '/agenda',
    texto: 'Agenda',
    Icono: CalendarMonthOutlinedIcon,
    permiso: 'citas:leer',
    atajo: 'a',
  },
  {
    a: '/pacientes',
    texto: 'Pacientes',
    Icono: PeopleAltOutlinedIcon,
    permiso: 'pacientes:leer',
    atajo: 'p',
    parcial: true,
  },
  {
    a: '/estudios',
    texto: 'Estudios',
    Icono: ScienceOutlinedIcon,
    permiso: 'colposcopias:leer',
    modulo: 'ginecologia-colposcopia',
    atajo: 'e',
  },
  {
    a: '/seguimiento',
    texto: 'Seguimiento',
    Icono: AssignmentTurnedInOutlinedIcon,
    permiso: 'seguimiento:leer',
    modulo: 'seguimiento',
  },
  {
    a: '/mensajes',
    texto: 'Mensajes',
    Icono: ForumOutlinedIcon,
    // Recepcion y medico lo tienen; lo clinico se filtra dentro.
    permiso: 'mensajes:administrativos',
    modulo: 'mensajeria',
    parcial: true,
  },
];

const CONSULTORIO: readonly Destino[] = [
  {
    a: '/consultorio',
    texto: 'Identidad del consultorio',
    Icono: StorefrontOutlinedIcon,
    permiso: 'organizacion:leer',
  },
  {
    a: '/consultorio/usuarios',
    texto: 'Usuarios',
    Icono: GroupsOutlinedIcon,
    permiso: 'usuarios:leer',
  },
  {
    a: '/consultorio/equipo',
    texto: 'Equipo y acceso',
    Icono: AdminPanelSettingsOutlinedIcon,
    permiso: 'organizacion:editar',
  },
  {
    a: '/consultorio/agenda',
    texto: 'Horarios y portal',
    Icono: ScheduleOutlinedIcon,
    permiso: 'citas:leer',
  },
  {
    a: '/consultorio/plantillas',
    texto: 'Plantillas de formulario',
    Icono: DescriptionOutlinedIcon,
    permiso: 'plantillas:leer',
  },
  {
    a: '/consultorio/bitacora',
    texto: 'Bitácora',
    // Sin check en el icono: el check es solo del exito.
    Icono: ReceiptLongOutlinedIcon,
    permiso: 'auditoria:leer',
  },
];

/**
 * Enlace de la barra lateral, como en Telmo (casa Gelux): la barra es clara y
 * el destino activo lleva el plano de seleccion del consultorio, su texto en el
 * color del consultorio y una barra de 3px. Sin check: el check es del exito.
 * El atajo de teclado se muestra al pasar el cursor o al enfocar, para no
 * llenar la barra de letras.
 */
function Enlace({ destino, alNavegar }: { destino: Destino; alNavegar?(): void }) {
  const { Icono, texto, a, atajo, parcial } = destino;

  return (
    <Stack
      component={NavLink}
      to={a}
      end={!parcial}
      onClick={alNavegar}
      aria-keyshortcuts={atajo}
      direction="row"
      alignItems="center"
      sx={{
        position: 'relative',
        gap: 'var(--space-4)',
        px: 'var(--space-4)',
        minHeight: 'var(--density-control-h)',
        borderRadius: 'var(--radius-md)',
        color: 'var(--text-secondary)',
        fontSize: 'var(--text-body-dense)',
        fontWeight: 500,
        textDecoration: 'none',
        transition: 'var(--transition-colors)',
        '& .MuiSvgIcon-root': { fontSize: 20, flexShrink: 0 },
        '& kbd': { opacity: 0, transition: 'var(--transition-colors)' },
        '&:hover': { bgcolor: 'var(--bg-hover)', color: 'var(--text-primary)' },
        '&:hover kbd, &:focus-visible kbd': { opacity: 1 },
        '&.active': {
          bgcolor: 'var(--bg-selected)',
          color: 'var(--accent-text)',
          fontWeight: 600,
        },
        '&.active::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 10,
          bottom: 10,
          width: 3,
          borderRadius: 'var(--radius-full)',
          bgcolor: 'var(--accent-emphasis)',
        },
      }}
    >
      <Icono fontSize="small" />
      {/* Se parte en dos lineas antes que cortarse: «Identidad del consultorio». */}
      <Typography
        component="span"
        sx={{
          py: 'var(--space-3)',
          fontSize: 'inherit',
          fontWeight: 'inherit',
          lineHeight: 'var(--leading-snug)',
          color: 'inherit',
          minWidth: 0,
        }}
      >
        {texto}
      </Typography>
      {atajo && (
        <>
          <Box sx={{ flexGrow: 1 }} />
          <Tecla>{atajo.toUpperCase()}</Tecla>
        </>
      )}
    </Stack>
  );
}

function Grupo({
  rotulo,
  destinos,
  alNavegar,
}: {
  rotulo: string;
  destinos: readonly Destino[];
  alNavegar?(): void;
}) {
  const { puede, tieneModulo } = useSesion();
  const visibles = destinos.filter(
    (destino) =>
      (!destino.permiso || puede(destino.permiso)) &&
      (!destino.modulo || tieneModulo(destino.modulo)),
  );
  if (visibles.length === 0) return null;

  return (
    <Box role="group" aria-label={rotulo}>
      <Typography
        component="span"
        aria-hidden
        sx={{
          display: 'block',
          px: 'var(--space-4)',
          pb: 'var(--space-3)',
          fontSize: 'var(--text-meta)',
          fontWeight: 600,
          letterSpacing: '0.06em',
          lineHeight: 'var(--leading-snug)',
          textTransform: 'uppercase',
          color: 'var(--text-tertiary)',
        }}
      >
        {rotulo}
      </Typography>
      <Stack sx={{ gap: 'var(--space-1)' }}>
        {visibles.map((destino) => (
          <Enlace key={destino.a} destino={destino} alNavegar={alNavegar} />
        ))}
      </Stack>
    </Box>
  );
}

/**
 * Marca del consultorio en la cabecera de la barra lateral, como el isotipo de
 * Telmo: el logo (si lo subio) a la izquierda y el nombre con «Expediente
 * clinico» debajo. Sin logo, sus iniciales en una ficha del color del
 * consultorio.
 */
function MarcaDeLaBarra({ alNavegar }: { alNavegar?(): void }) {
  const { marca } = useMarca();
  return (
    <Stack
      component={NavLink}
      to="/"
      onClick={alNavegar}
      aria-label={`${marca.nombreComercial} · ir al inicio`}
      direction="row"
      alignItems="center"
      sx={{
        gap: 'var(--space-4)',
        px: 'var(--space-3)',
        py: 'var(--space-3)',
        borderRadius: 'var(--radius-md)',
        textDecoration: 'none',
        '&:hover': { bgcolor: 'var(--bg-hover)' },
      }}
    >
      {marca.logoUrl ? (
        // El logo del consultorio viene pensado para papel: va en su ficha clara.
        <Box
          sx={{
            flexShrink: 0,
            p: 'var(--space-1)',
            borderRadius: 'var(--radius-sm)',
            bgcolor: 'var(--brand-logo-bg)',
          }}
        >
          <Logo alto={32} />
        </Box>
      ) : (
        <Box
          aria-hidden
          sx={{
            display: 'grid',
            placeItems: 'center',
            width: 36,
            height: 36,
            flexShrink: 0,
            borderRadius: 'var(--radius-sm)',
            ...FICHA_DE_PULSO,
            fontFamily: 'var(--font-head)',
            fontSize: 'var(--text-body-dense)',
            fontWeight: 700,
          }}
        >
          {iniciales(marca.nombreComercial)}
        </Box>
      )}
      <Box sx={{ minWidth: 0 }}>
        <Typography
          component="span"
          sx={{
            display: 'block',
            fontFamily: 'var(--font-head)',
            fontSize: '1rem',
            fontWeight: 600,
            lineHeight: 'var(--leading-snug)',
            color: 'var(--text-primary)',
            overflowWrap: 'anywhere',
          }}
        >
          {marca.nombreComercial}
        </Typography>
        <Typography
          component="span"
          sx={{
            display: 'block',
            fontSize: 'var(--text-meta)',
            fontWeight: 500,
            color: 'var(--text-tertiary)',
          }}
        >
          Expediente clínico
        </Typography>
      </Box>
    </Stack>
  );
}

/**
 * Tarjeta del pie de la barra: el consultorio y el rol de quien esta dentro,
 * como la tarjeta de agencia de Telmo.
 */
function TarjetaDeConsultorio() {
  const { usuario, consultorio } = useSesion();
  const nombre = consultorio?.nombre ?? '';
  const rol = usuario ? (ROLES_LEGIBLES[usuario.rol] ?? usuario.rol) : '';
  return (
    <Stack
      direction="row"
      alignItems="center"
      sx={{
        gap: 'var(--space-4)',
        p: 'var(--space-4)',
        border: '1px solid var(--border-hairline)',
        borderRadius: 'var(--radius-lg)',
        bgcolor: 'var(--bg-raised)',
      }}
    >
      <Avatar
        aria-hidden
        sx={{
          width: 32,
          height: 32,
          fontSize: 'var(--text-meta)',
          fontWeight: 700,
          ...FICHA_DE_PULSO,
        }}
      >
        {iniciales(nombre)}
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Typography noWrap sx={{ fontSize: 'var(--text-body-dense)', fontWeight: 600 }}>
          {nombre}
        </Typography>
        <Typography noWrap sx={{ fontSize: 'var(--text-meta)', color: 'var(--text-secondary)' }}>
          {rol}
        </Typography>
      </Box>
    </Stack>
  );
}

/**
 * Barra lateral en el armazon de Pulso, como la del personal de
 * GestionHospitalaria: el pino de la marca con el destello del pulso, el
 * consultorio arriba, los destinos agrupados al centro y, abajo, quien esta
 * dentro y la firma del producto. En escritorio es fija; por debajo de 1024px
 * va dentro de un cajon.
 */
function BarraLateral({ alNavegar }: { alNavegar?(): void }) {
  return (
    <Stack
      component="nav"
      aria-label="Navegación principal"
      sx={{
        width: CHASIS.rielAncho,
        maxWidth: '100%',
        flexShrink: 0,
        height: '100%',
        ...ARMAZON_PULSO,
        scrollbarColor: 'var(--bg-on-brand-selected) transparent',
        pt: 'var(--space-5)',
        pb: 'var(--space-5)',
        px: 'var(--space-5)',
        overflowY: 'auto',
        overflowX: 'hidden',
      }}
    >
      <MarcaDeLaBarra alNavegar={alNavegar} />
      <Stack sx={{ gap: 'var(--space-7)', mt: 'var(--space-6)' }}>
        <Grupo rotulo="Trabajo" destinos={TRABAJO} alNavegar={alNavegar} />
        <Grupo rotulo="Consultorio" destinos={CONSULTORIO} alNavegar={alNavegar} />
      </Stack>

      <Box sx={{ flexGrow: 1, minHeight: 'var(--space-7)' }} />
      <TarjetaDeConsultorio />
      <Box sx={{ mt: 'var(--space-5)', px: 'var(--space-3)' }}>
        <FirmaDePulso />
      </Box>
    </Stack>
  );
}

/** Menu de tema: la opcion vigente se marca como seleccion, sin check. */
function MenuDeTema() {
  const { modo, preferencia, elegir } = useApariencia();
  const [ancla, setAncla] = useState<HTMLElement | null>(null);

  return (
    <>
      <Tooltip title="Tema">
        <IconButton
          onClick={(evento) => setAncla(evento.currentTarget)}
          sx={BOTON_REDONDO}
          aria-label="Cambiar el tema"
          aria-haspopup="menu"
          aria-expanded={ancla ? 'true' : undefined}
        >
          {preferencia === 'automatico' ? (
            <SettingsBrightnessRoundedIcon fontSize="small" />
          ) : modo === 'claro' ? (
            <LightModeRoundedIcon fontSize="small" />
          ) : (
            <DarkModeRoundedIcon fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
      <Menu anchorEl={ancla} open={Boolean(ancla)} onClose={() => setAncla(null)}>
        {OPCIONES_TEMA.map(({ valor, texto, Icono }) => (
          <MenuItem
            key={valor}
            role="menuitemradio"
            aria-checked={preferencia === valor}
            selected={preferencia === valor}
            onClick={() => {
              elegir(valor);
              setAncla(null);
            }}
          >
            <Icono fontSize="small" />
            {texto}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

/** Quien esta dentro, su rol y su consultorio. Cuenta y salida. */
function MenuDeUsuario({ compacto }: { compacto: boolean }) {
  const { usuario, consultorio, salir } = useSesion();
  const navegar = useNavigate();
  const [ancla, setAncla] = useState<HTMLElement | null>(null);
  const nombre = usuario ? nombreParaMostrar(usuario) : '';
  const rol = usuario ? (ROLES_LEGIBLES[usuario.rol] ?? usuario.rol) : '';

  return (
    <>
      <ButtonBase
        onClick={(evento) => setAncla(evento.currentTarget)}
        aria-label={`Cuenta de ${nombre}`}
        aria-haspopup="menu"
        aria-expanded={ancla ? 'true' : undefined}
        sx={{
          gap: '10px',
          minHeight: 'var(--density-control-h)',
          px: compacto ? '2px' : '8px',
          borderRadius: 'var(--radius-field)',
          color: 'var(--text-primary)',
          transition: 'var(--transition-colors)',
          '&:hover': { bgcolor: 'var(--bg-hover)' },
        }}
      >
        <Avatar aria-hidden sx={{ width: 34, height: 34 }}>
          {iniciales(usuario?.nombre ?? '')}
        </Avatar>
        {!compacto && (
          <Box sx={{ minWidth: 0, textAlign: 'left', maxWidth: 200 }}>
            <Typography
              noWrap
              sx={{ fontSize: 'var(--text-body-dense)', fontWeight: 600, lineHeight: 1.25 }}
            >
              {nombre}
            </Typography>
            <Typography
              noWrap
              variant="caption"
              component="div"
              sx={{ color: 'var(--text-secondary)', lineHeight: 1.25 }}
            >
              {rol}
            </Typography>
          </Box>
        )}
      </ButtonBase>

      <Menu
        anchorEl={ancla}
        open={Boolean(ancla)}
        onClose={() => setAncla(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 240, maxWidth: 'min(88vw, 320px)' } } }}
      >
        <Box sx={{ px: '10px', pt: '6px', pb: '10px' }}>
          <Typography sx={{ fontSize: 'var(--text-body-dense)', fontWeight: 600 }}>
            {nombre}
          </Typography>
          <Typography
            variant="caption"
            display="block"
            sx={{ color: 'var(--text-secondary)', overflowWrap: 'anywhere' }}
          >
            {usuario?.correo}
          </Typography>
          <Typography variant="caption" display="block" sx={{ color: 'var(--text-secondary)' }}>
            {rol} · {consultorio?.nombre}
          </Typography>
        </Box>
        <Divider sx={{ mb: '4px' }} />
        <MenuItem
          onClick={() => {
            setAncla(null);
            navegar('/cuenta');
          }}
        >
          <ListItemIcon>
            <BadgeRoundedIcon fontSize="small" />
          </ListItemIcon>
          Mi cuenta y contraseña
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAncla(null);
            salir();
            navegar('/acceso');
          }}
        >
          <ListItemIcon>
            <LogoutRoundedIcon fontSize="small" />
          </ListItemIcon>
          Cerrar sesión
        </MenuItem>
      </Menu>
    </>
  );
}

/**
 * Buscador de paciente de la barra superior. Lleva al expediente elegido.
 * Atajo: `/`, como en la mayoria de herramientas de trabajo.
 */
function BuscadorGlobal({ alElegir }: { alElegir?(): void }) {
  const navegar = useNavigate();
  const campo = useRef<HTMLInputElement>(null);
  useAtajo('/', () => campo.current?.focus());

  return (
    <Box
      sx={{
        '& .MuiOutlinedInput-root': {
          borderRadius: 'var(--radius-full)',
          bgcolor: 'var(--bg-raised)',
          minHeight: 42,
          pl: 'var(--space-5)',
        },
        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--border-subtle)' },
        '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: 'var(--border-strong)',
        },
      }}
    >
      <BuscadorDePaciente
        valor={null}
        etiqueta="Buscar paciente"
        etiquetaOculta
        referencia={campo}
        alElegir={(paciente) => {
          if (!paciente) return;
          navegar(`/pacientes/${paciente.id}`);
          alElegir?.();
        }}
      />
    </Box>
  );
}

/**
 * Armazon de la aplicacion (sistema de Pulso).
 *
 * - Escritorio (>= 1024px): barra lateral de 248px a todo el alto, en el color
 *   del consultorio, y a su derecha la barra superior de 68px (buscador y
 *   cuenta) sobre el contenido.
 * - Por debajo de 1024px la barra lateral va en un cajon que abre «Menú» en la
 *   barra superior; se cierra con Escape, con el velo o al elegir un destino.
 *   No hay barra inferior.
 *
 * Dentro de una paciente, la cinta con su identidad queda fija arriba del
 * contenido, incluida la captura de un estudio.
 */
/**
 * Aviso del estado comercial (F5.1). Solo cuando limita o va a limitar algo; la
 * lectura y la exportación siguen disponibles en cualquier estado.
 */
const AVISO_COMERCIAL: Partial<Record<EstadoComercial, string>> = {
  'pago-pendiente':
    'La suscripción del consultorio tiene un pago pendiente. Todo sigue funcionando durante el periodo de gracia.',
  restringida:
    'La suscripción del consultorio está restringida: puede consultar, exportar y seguir atendiendo, pero no subir archivos ni publicar al portal.',
  'cancelacion-programada':
    'La suscripción del consultorio terminará al cerrar su periodo. Puede exportar sus expedientes en cualquier momento.',
  terminada: 'La suscripción del consultorio terminó. Puede consultar y exportar sus expedientes.',
};

/**
 * Donde esta la persona, para la miga de pan: el destino de la barra cuyo
 * camino coincide mas largo con la ruta, y su grupo.
 */
export function ubicacionDe(pathname: string): { grupo: string; texto: string } | null {
  let mejor: { grupo: string; texto: string; largo: number } | null = null;
  for (const [grupo, destinos] of [
    ['Trabajo', TRABAJO],
    ['Consultorio', CONSULTORIO],
  ] as const) {
    for (const destino of destinos) {
      const coincide =
        destino.a === '/'
          ? pathname === '/'
          : pathname === destino.a || pathname.startsWith(`${destino.a}/`);
      if (coincide && (!mejor || destino.a.length > mejor.largo)) {
        mejor = { grupo, texto: destino.texto, largo: destino.a.length };
      }
    }
  }
  return mejor && { grupo: mejor.grupo, texto: mejor.texto };
}

/** Boton redondo de la barra superior, como los de Telmo. */
const BOTON_REDONDO = {
  width: 40,
  height: 40,
  border: '1px solid var(--border-subtle)',
  bgcolor: 'var(--bg-raised)',
  color: 'var(--text-secondary)',
  '&:hover': { bgcolor: 'var(--bg-hover)', color: 'var(--text-primary)' },
} as const;

/**
 * Barra inferior del telefono (Telmo): los primeros destinos de trabajo y
 * «Más», que abre la barra completa. El destino activo lleva el plano de
 * seleccion.
 */
function BarraInferior({ alAbrirMas }: { alAbrirMas(): void }) {
  const { puede, tieneModulo } = useSesion();
  const visibles = TRABAJO.filter(
    (destino) =>
      (!destino.permiso || puede(destino.permiso)) &&
      (!destino.modulo || tieneModulo(destino.modulo)),
  ).slice(0, 4);

  const estilo = {
    flex: '1 1 0',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '2px',
    minHeight: 52,
    borderRadius: 'var(--radius-md)',
    color: 'var(--text-secondary)',
    fontSize: 'var(--text-meta)',
    fontWeight: 600,
    textDecoration: 'none',
    '& .MuiSvgIcon-root': { fontSize: 22 },
    '&.active': { bgcolor: 'var(--bg-selected)', color: 'var(--accent-text)' },
  } as const;

  return (
    <Stack
      component="nav"
      aria-label="Navegación"
      direction="row"
      className="no-imprimir"
      sx={{
        flexShrink: 0,
        gap: 'var(--space-1)',
        px: 'var(--space-2)',
        pt: 'var(--space-2)',
        pb: 'calc(var(--space-2) + env(safe-area-inset-bottom))',
        bgcolor: 'var(--bg-chrome)',
        borderTop: '1px solid var(--border-hairline)',
      }}
    >
      {visibles.map(({ a, texto, Icono, parcial }) => (
        <Box key={a} component={NavLink} to={a} end={!parcial} sx={estilo}>
          <Icono fontSize="small" />
          <Box
            component="span"
            sx={{
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {texto}
          </Box>
        </Box>
      ))}
      <ButtonBase onClick={alAbrirMas} aria-haspopup="dialog" sx={estilo}>
        <MoreHorizRoundedIcon fontSize="small" />
        <Box component="span">Más</Box>
      </ButtonBase>
    </Stack>
  );
}

export function Cascara() {
  const escritorio = useMediaQuery('(min-width: 1024px)');
  const movil = useMediaQuery('(max-width: 767.95px)');
  const navegar = useNavigate();
  const { pathname } = useLocation();
  const { puede, tieneModulo, usuario, estadoComercial } = useSesion();
  const { marca } = useMarca();

  const [cajonAbierto, setCajonAbierto] = useState(false);
  const [buscando, setBuscando] = useState(false);

  useAtajo('h', () => navegar('/'));
  useAtajo('p', () => navegar('/pacientes'), { activo: puede('pacientes:leer') });
  useAtajo('a', () => navegar('/agenda'), { activo: puede('citas:leer') });
  useAtajo('e', () => navegar('/estudios'), {
    activo: puede('colposcopias:leer') && tieneModulo('ginecologia-colposcopia'),
  });

  // Al pasar a escritorio el cajon sobra: la barra ya esta fija.
  useEffect(() => {
    if (escritorio) setCajonAbierto(false);
  }, [escritorio]);

  // Con la contrasena provisional no hay a donde ir mas que a cambiarla: la
  // navegacion se retira en lugar de rebotar cada clic a /cuenta.
  const restringido = Boolean(usuario?.debeCambiarContrasena);

  // La cinta de paciente sale de la ruta, no de un contexto que cada pantalla
  // tenga que recordar rellenar. `nuevo` no es un identificador.
  const enPaciente = useMatch({ path: '/pacientes/:pacienteId', end: false });
  const pacienteId =
    enPaciente?.params.pacienteId && enPaciente.params.pacienteId !== 'nuevo'
      ? enPaciente.params.pacienteId
      : '';
  const ubicacion = ubicacionDe(pathname);
  const conBarraInferior = movil && !restringido;

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100dvh',
        overflow: 'hidden',
        bgcolor: 'var(--bg-canvas)',
      }}
    >
      <a className="saltar-al-contenido" href="#contenido">
        Saltar al contenido
      </a>

      {escritorio && !restringido && (
        <Box className="no-imprimir" sx={{ flexShrink: 0, height: '100%' }}>
          <BarraLateral />
        </Box>
      )}

      <Box
        sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column', height: '100%' }}
      >
        {/* Barra superior: donde esta a la izquierda; busqueda, tema y cuenta a la derecha. */}
        <Stack
          component="header"
          direction="row"
          alignItems="center"
          className="no-imprimir"
          sx={{
            flexShrink: 0,
            gap: { xs: 'var(--space-3)', md: 'var(--space-4)' },
            minHeight: CHASIS.cabeceraAlto,
            px: { xs: 'var(--space-5)', md: 'var(--space-7)', lg: 'var(--space-9)' },
            bgcolor: 'var(--bg-canvas)',
            borderBottom: '1px solid var(--border-hairline)',
            zIndex: 'var(--z-cabecera)',
          }}
        >
          {!escritorio && !movil && !restringido && (
            <IconButton
              aria-label="Menú"
              aria-haspopup="dialog"
              aria-expanded={cajonAbierto ? 'true' : undefined}
              onClick={() => setCajonAbierto(true)}
              sx={BOTON_REDONDO}
            >
              <MenuRoundedIcon fontSize="small" />
            </IconButton>
          )}

          {movil || restringido || !ubicacion ? (
            <Typography
              component="span"
              noWrap
              sx={{
                minWidth: 0,
                flexShrink: 1,
                fontFamily: 'var(--font-head)',
                fontSize: '1rem',
                fontWeight: 600,
                color: 'var(--text-primary)',
              }}
            >
              {movil && ubicacion ? ubicacion.texto : marca.nombreComercial}
            </Typography>
          ) : (
            <Stack
              component="nav"
              aria-label="Ubicación"
              direction="row"
              alignItems="center"
              sx={{ gap: 'var(--space-3)', minWidth: 0, fontSize: 'var(--text-body-dense)' }}
            >
              <Box component="span" sx={{ color: 'var(--text-secondary)' }}>
                {ubicacion.grupo}
              </Box>
              <Box component="span" aria-hidden sx={{ color: 'var(--text-tertiary)' }}>
                /
              </Box>
              <Box
                component="span"
                aria-current="page"
                sx={{ color: 'var(--text-primary)', fontWeight: 500 }}
              >
                {ubicacion.texto}
              </Box>
            </Stack>
          )}

          <Box sx={{ flexGrow: 1 }} />

          {!restringido && puede('pacientes:leer') && !movil && (
            <Box sx={{ flex: '0 1 380px', minWidth: 220 }}>
              <BuscadorGlobal />
            </Box>
          )}
          {!restringido && puede('pacientes:leer') && movil && (
            <IconButton
              aria-label="Buscar paciente"
              onClick={() => setBuscando(true)}
              sx={BOTON_REDONDO}
            >
              <SearchRoundedIcon fontSize="small" />
            </IconButton>
          )}
          <MenuDeTema />
          <Box
            aria-hidden
            sx={{
              display: { xs: 'none', sm: 'block' },
              width: '1px',
              alignSelf: 'stretch',
              my: 'var(--space-6)',
              bgcolor: 'var(--border-subtle)',
            }}
          />
          <MenuDeUsuario compacto={movil} />
        </Stack>

        <Box
          component="main"
          id="contenido"
          tabIndex={-1}
          sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto', outline: 'none' }}
        >
          {estadoComercial && AVISO_COMERCIAL[estadoComercial] && (
            <Alert severity="warning" square sx={{ borderRadius: 0 }}>
              {AVISO_COMERCIAL[estadoComercial]}
            </Alert>
          )}
          {pacienteId && !restringido && <CintaDePaciente pacienteId={pacienteId} />}
          <Box
            sx={{
              // Margenes del contenido: 16px de canal en movil, 40px en escritorio.
              px: { xs: 'var(--space-5)', md: 'var(--space-7)', lg: 'var(--space-9)' },
              pt: { xs: 'var(--space-6)', md: 'var(--space-8)' },
              pb: { xs: 'var(--space-8)', md: 'var(--space-10)' },
            }}
          >
            <Box sx={{ maxWidth: CHASIS.anchoTabla, mx: 'auto' }}>
              {/* La frontera va DENTRO del armazon: si estuviera fuera, la
                  navegacion desapareceria cada vez que se pide una pantalla
                  nueva, o cada vez que una truena. */}
              <FronteraDeContenido llave={pathname}>
                <Suspense fallback={<Cargando />}>
                  <Outlet />
                </Suspense>
              </FronteraDeContenido>
            </Box>
          </Box>
        </Box>

        {conBarraInferior && <BarraInferior alAbrirMas={() => setCajonAbierto(true)} />}
      </Box>

      {/* Cajon por debajo de 1024px: la misma barra lateral. En el telefono lo
          abre «Más» de la barra inferior. */}
      <Drawer
        anchor="left"
        open={!escritorio && !restringido && cajonAbierto}
        onClose={() => setCajonAbierto(false)}
        slotProps={{
          paper: {
            sx: {
              width: 'min(20rem, 86vw)',
              bgcolor: 'var(--bg-chrome)',
              borderRadius: 0,
              border: 0,
            },
          },
        }}
      >
        <Box sx={{ position: 'relative', height: '100%', '& > nav': { width: '100%' } }}>
          <IconButton
            aria-label="Cerrar el menú"
            onClick={() => setCajonAbierto(false)}
            sx={{
              position: 'absolute',
              top: 'var(--space-4)',
              right: 'var(--space-4)',
              zIndex: 1,
            }}
          >
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
          <BarraLateral alNavegar={() => setCajonAbierto(false)} />
        </Box>
      </Drawer>

      {/* Busqueda del movil: dialogo a pantalla completa, con el teclado arriba. */}
      <Dialog open={movil && buscando} onClose={() => setBuscando(false)} fullScreen>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: '8px', pr: '8px' }}>
          <Box component="span" sx={{ flexGrow: 1 }}>
            Buscar paciente
          </Box>
          <IconButton aria-label="Cerrar la búsqueda" onClick={() => setBuscando(false)}>
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: '8px' }}>
            <BuscadorDePaciente
              valor={null}
              etiqueta="Nombre, expediente o teléfono"
              alElegir={(paciente) => {
                if (!paciente) return;
                setBuscando(false);
                navegar(`/pacientes/${paciente.id}`);
              }}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
